import React, { useCallback, useEffect, useRef, useState } from "react";
import useConsent from "./useConsent";
import { COOKIE_CATEGORIES } from "./ConsentContext";

const CATEGORY_DESCRIPTIONS = {
  essential: "Enables security and basic functionality.",
  analytics: "Enables tracking of site performance.",
  advertising: "Enables ads personalisation and tracking.",
  functional: "Enables personalised features and preferences.",
};

const buildLocalState = (consentState, defaultCategories) =>
  Object.fromEntries(
    Object.entries(COOKIE_CATEGORIES).map(([k, def]) => [
      k,
      consentState?.[k] ?? defaultCategories?.[k] ?? def.default,
    ]),
  );

const PreferencesPanel = () => {
  const {
    consentState,
    updateCategories,
    closePreferences,
    isPanelOpen,
    defaultCategories,
  } = useConsent();
  const [localCats, setLocalCats] = useState(() =>
    buildLocalState(consentState, defaultCategories),
  );
  const panelRef = useRef(null);

  useEffect(() => {
    if (isPanelOpen)
      setLocalCats(buildLocalState(consentState, defaultCategories));
  }, [isPanelOpen, consentState, defaultCategories]);

  const handleCancel = useCallback(
    () => closePreferences(),
    [closePreferences],
  );
  const handleSave = useCallback(
    () => updateCategories(localCats),
    [updateCategories, localCats],
  );
  const handleToggle = useCallback((cat) => {
    if (COOKIE_CATEGORIES[cat].locked) return;
    setLocalCats((prev) => ({ ...prev, [cat]: !prev[cat] }));
  }, []);

  // Focus trap
  useEffect(() => {
    if (!isPanelOpen) return;
    const panel = panelRef.current;
    if (!panel) return;
    const focusable = panel.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
    );
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    first?.focus();
    const onTab = (e) => {
      if (e.key !== "Tab") return;
      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last?.focus();
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault();
          first?.focus();
        }
      }
    };
    panel.addEventListener("keydown", onTab);
    return () => panel.removeEventListener("keydown", onTab);
  }, [isPanelOpen]);

  // Escape closes
  useEffect(() => {
    if (!isPanelOpen) return;
    const onKeyDown = (e) => {
      if (e.key === "Escape") handleSave();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [isPanelOpen, handleSave]);

  if (!isPanelOpen) return null;

  return (
    /* Backdrop */
    <div
      className="fixed inset-0 z-[10000] flex items-end justify-center sm:px-4"
      style={{ background: "rgba(0,0,0,0.5)" }}
      onClick={(e) => {
        if (e.target === e.currentTarget) handleSave();
      }}
    >
      {/* Modal — bottom sheet on mobile, centered on desktop */}
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label="Cookie preferences"
        className="
          w-full sm:max-w-5xl
          max-h-[92vh] sm:max-h-[85vh]
          overflow-y-auto
          bg-dark
          rounded-t-2xl sm:rounded-2xl
          shadow-card
          p-5 sm:p-7
          flex flex-col
          sm:mb-10
        "
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="relative flex items-center justify-between mb-4">
          <button
            onClick={handleCancel}
            aria-label="Go back"
            className="text-text-muted hover:text-white transition-colors text-sm flex items-center gap-1"
          >
            ← Back
          </button>
          <p className="font-heading font-semibold text-white text-base absolute left-1/2 -translate-x-1/2 whitespace-nowrap">
            Cookie Settings
          </p>
          <button
            onClick={handleCancel}
            aria-label="Close cookie preferences"
            className="text-text-muted hover:text-white transition-colors text-lg leading-none p-1"
          >
            &#x2715;
          </button>
        </div>

        {/* Mobile drag handle hint */}
        <div className="flex justify-center mb-3 sm:hidden">
          <div className="w-10 h-1 rounded-full bg-dark-light" />
        </div>

        {/* Description */}
        <p className="text-white text-sm leading-relaxed mb-5">
          Our website uses cookies to distinguish you from other users. This
          helps us provide a more personalised experience and improve our site.
          You can choose not to allow some types of cookies.
        </p>

        {/* Categories */}
        <div className="flex flex-col gap-3 flex-1">
          {Object.entries(COOKIE_CATEGORIES)
            .filter(([, def]) => def.showInBanner)
            .map(([key, def]) => {
              const isOn = !!localCats[key];
              return (
                <div
                  key={key}
                  className="flex items-center justify-between bg-dark rounded-xl px-4 py-3 gap-3"
                >
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-white mb-0.5">
                      {def.label}
                    </p>
                    <p className="text-xs text-text-muted">
                      {CATEGORY_DESCRIPTIONS[key]}
                    </p>
                  </div>

                  <div className="flex items-center gap-2 shrink-0">
                    {def.locked ? (
                      <>
                        <span className="hidden sm:inline text-xs font-medium px-2 py-0.5 rounded-full text-text-muted border border-dark-light">
                          REQUIRED
                        </span>
                        <button
                          role="switch"
                          aria-checked={true}
                          aria-label={`${def.label} cookies`}
                          disabled
                          className="relative inline-flex items-center w-11 h-6 rounded-full cursor-not-allowed shrink-0"
                          style={{ background: "#22c55e" }}
                        >
                          <span className="absolute right-1 w-4 h-4 rounded-full bg-white" />
                        </button>
                      </>
                    ) : (
                      <button
                        role="switch"
                        aria-checked={isOn}
                        aria-label={`${def.label} cookies`}
                        onClick={() => handleToggle(key)}
                        className="relative inline-flex items-center w-11 h-6 rounded-full shrink-0 transition-colors duration-200 cursor-pointer border-none"
                        style={{ background: isOn ? "#22c55e" : "#393d44" }}
                      >
                        <span
                          className="absolute w-4 h-4 rounded-full bg-white transition-all duration-200"
                          style={{ left: isOn ? "24px" : "4px" }}
                        />
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
        </div>

        {/* Save button */}
        <button
          onClick={handleSave}
          className="px-6 py-3 rounded-xl text-sm font-semibold text-white bg-primary hover:bg-primary-hover transition-colors"
        >
          Save preferences
        </button>
      </div>
    </div>
  );
};

export default PreferencesPanel;
